import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface PasscodeEntryProps {
  pageTitle: string;
  onVerify: (passcode: string) => Promise<boolean>;
  brandColor?: string;
  brandColorContrast?: string;
  logoUrl?: string;
  companyName?: string;
}

export const PasscodeEntry = ({
  pageTitle,
  onVerify,
  brandColor,
  brandColorContrast,
  logoUrl,
  companyName,
}: PasscodeEntryProps) => {
  const [passcode, setPasscode] = useState(['', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const MAX_ATTEMPTS = 5;
  const isLocked = attempts >= MAX_ATTEMPTS;

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newPasscode = [...passcode];
    newPasscode[index] = value;
    setPasscode(newPasscode);
    setError(null);

    // Auto-advance to next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 3 && newPasscode.every(d => d !== '')) {
      handleVerify(newPasscode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !passcode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length === 4) {
      const newPasscode = pastedData.split('');
      setPasscode(newPasscode);
      inputRefs.current[3]?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (code: string) => {
    if (isLocked) return;
    
    setIsVerifying(true);
    setError(null);

    try {
      const isValid = await onVerify(code);
      if (!isValid) {
        setAttempts(prev => prev + 1);
        setError(attempts + 1 >= MAX_ATTEMPTS 
          ? "Too many failed attempts. Please contact your administrator."
          : "Incorrect passcode. Please try again.");
        setPasscode(['', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
      setPasscode(['', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt={companyName ? `${companyName} logo` : "Company logo"}
              className="h-12 mx-auto object-contain"
              loading="eager"
            />
          )}
          
          {companyName && (
            <p className="text-sm font-medium text-muted-foreground">{companyName}</p>
          )}
          
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{pageTitle}</h1>
        </div>

        {/* Passcode Card */}
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-8 pb-8 px-6 md:px-8">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Lock className="h-7 w-7 text-primary" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h2 className="text-lg font-semibold">Enter Access Code</h2>
                <p className="text-sm text-muted-foreground">
                  This feedback page is protected. Please enter the 4-digit access code provided by your administrator.
                </p>
              </div>

              {/* Passcode Inputs */}
              <div 
                className="flex justify-center gap-3"
                onPaste={handlePaste}
              >
                {passcode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { inputRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={isVerifying || isLocked}
                    className={cn(
                      "w-14 h-16 text-center text-2xl font-bold border-2 rounded-lg",
                      "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                      "transition-all duration-200",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      error ? "border-destructive" : "border-input",
                      digit && "bg-primary/5 border-primary/30"
                    )}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center justify-center gap-2 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              {/* Verify Button */}
              <Button
                onClick={() => handleVerify(passcode.join(''))}
                disabled={passcode.some(d => d === '') || isVerifying || isLocked}
                className="w-full"
                size="lg"
                style={brandColor ? { 
                  backgroundColor: brandColor,
                  color: brandColorContrast || '#ffffff'
                } : undefined}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  "Access Feedback Page"
                )}
              </Button>

              {/* Attempts remaining */}
              {attempts > 0 && !isLocked && (
                <p className="text-xs text-muted-foreground">
                  {MAX_ATTEMPTS - attempts} attempts remaining
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Don't have the access code? Contact your team administrator.
        </p>
      </div>
    </div>
  );
};
